import React, { useState, useEffect, useMemo, useRef } from 'react';
import { css } from '@emotion/react';
import GoogleMap, { Circle, DirectionsRenderer } from '@/components/GoogleMap';
import { useDriverLocation } from '@/hooks/useDriverLocation';
import FTLStopHelpers from '@/utilities/FTLStopHelpers';
import { Truck, Home, PickUp } from '@/components/DispatchPlan/map/MapMarkers';
import { colors, typography } from '@/styles';
import { COMPLETE_ORDER_STATUSES, genAttributes, IN_TRANSIT_ORDER_STATUSES } from '@onward-delivery/core';
import { LastUpdatedControl } from '../GoogleMap/controls/LastUpdatedControl';
import { APPROXIMATE_FACILITY_LOCATION } from '@/constants/featureFlags';
import FacilityMarker from './FacilityMarker';
import { APPROX_MAX_ZOOM, APPROX_RADIUS_M, genApproximateCenter, selectFacilityDisplay } from './utilities';

const DEFAULT_CENTER = { lat: 39.74, lng: -104.99 };
const DEFAULT_ZOOM = 17;
const TWELVE_HOURS = 43200000;

const TrackingMap = ({ order, route }) => {
    const mapRef = useRef();
    const [bounds, setBounds] = useState();
    const [directions, setDirections] = useState();

    const [customerLocation, pickupLocation] = useMemo(() => {
        if (!order) return [null, null];

        const { lat, long } = genAttributes(order || {});
        const { lat: pulat, long: pulong } = genAttributes(order, 'PICKUP');

        return [
            { lat: order[lat], lng: order[long] },
            { lat: order[pulat], lng: order[pulong] },
        ];
    }, [order]);

    const pickupComplete = useMemo(() => {
        return [...IN_TRANSIT_ORDER_STATUSES, ...COMPLETE_ORDER_STATUSES].includes(order?.order_status);
    }, [order]);

    /*
     * Shipper- or carrier-controlled: draw the final-mile facility as an approximate area rather
     * than a pin on its front door, so customers can't drive to it for a self-pickup (ENG-9599).
     */
    const approximateFacility = useMemo(() => {
        return !!(
            order?.order_shipper?.user?.circles?.[APPROXIMATE_FACILITY_LOCATION] ||
            order?.order_carrier?.user?.circles?.[APPROXIMATE_FACILITY_LOCATION]
        );
    }, [order]);

    /*
     * Rolled once per page load, not per render: this component re-renders on every driver
     * coordinate the subscription pushes, and re-rolling there would visibly walk the circle around
     * the map while the customer watches it.
     */
    const approxCenter = useMemo(() => {
        if (!approximateFacility) return null;
        return genApproximateCenter(pickupLocation);
    }, [approximateFacility, pickupLocation?.lat, pickupLocation?.lng]);

    const liveTracking = useMemo(() => {
        if (!order) return false;

        return (
            ![...COMPLETE_ORDER_STATUSES].includes(order.order_status) &&
            !!order.delivery_date &&
            !!order.del_window_start &&
            !!order.del_window_end &&
            Date.now() >= new Date(order.del_window_start).getTime() - TWELVE_HOURS &&
            Date.now() <= new Date(order.del_window_end).getTime() + TWELVE_HOURS
        );
    }, [order]);

    const { location } = useDriverLocation(route ? route?.route_id : order.order_id, {
        disabled: !liveTracking,
    });

    /*
     * Whether the facility is shaded, and where the truck goes. Pure and unit tested in
     * ./utilities — see selectFacilityDisplay for why the circle outlives the pickup status.
     */
    const { hideFacility, truckPosition } = useMemo(
        () => selectFacilityDisplay({ approxCenter, pickupComplete, location }),
        [approxCenter, pickupComplete, location]
    );

    /*
     * Set map bounds to show both Truck and Customer
     * This is only done within 2 hrs of the delivery window
     */
    useEffect(() => {
        setBounds([
            ...(customerLocation ? [customerLocation] : []),
            ...(liveTracking && truckPosition ? [truckPosition] : []),
        ]);
    }, [liveTracking, truckPosition, customerLocation]);

    /*
     * Get Directions from Truck to Customer
     * This is only shown when the customer is the next stop on the route
     *
     * Suppressed entirely while the facility is hidden — before pickup the line would run to the
     * exact address the circle exists to hide, and after it the line would start from that address
     * while the truck is still sitting on the yard.
     */
    useEffect(() => {
        if (hideFacility) return;

        const destination = pickupComplete ? customerLocation : pickupLocation;
        if (liveTracking && destination && truckPosition && !directions) {
            FTLStopHelpers.getCustomerDirections(truckPosition, destination).then((result) => {
                console.debug(result);
                if (result.status === 'OK') setDirections(result);
            });
        }
    }, [liveTracking, order, truckPosition, customerLocation, pickupComplete, pickupLocation, hideFacility]);

    return (
        <div
            css={css`
                position: relative;
                height: 100%;
                width: 100%;
            `}
        >
            <GoogleMap
                ref={mapRef}
                bounds={bounds}
                center={!bounds?.length ? customerLocation || DEFAULT_CENTER : undefined}
                zoom={bounds?.length > 0 ? undefined : DEFAULT_ZOOM}
                maxZoom={hideFacility ? APPROX_MAX_ZOOM : undefined}
                mapTypeControl={false}
                fullscreenControl={false}
                zoomControl={false}
                streetViewControl={false}
            >
                {customerLocation && <Home position={customerLocation} />}
                {hideFacility ? (
                    <>
                        <Circle
                            center={approxCenter}
                            radius={APPROX_RADIUS_M}
                            clickable={false}
                            strokeColor={colors.greens.primary}
                            strokeOpacity={1}
                            strokeWeight={2}
                            fillColor={colors.greens.primary}
                            fillOpacity={0.3}
                        />
                        <FacilityMarker position={approxCenter} />
                    </>
                ) : (
                    !pickupComplete && <PickUp position={pickupLocation} />
                )}
                {liveTracking && truckPosition && <Truck key={'truck-marker'} position={truckPosition} rotation={90} />}
                {directions && (
                    <DirectionsRenderer
                        directions={directions}
                        options={{
                            suppressMarkers: true,
                            polylineOptions: {
                                strokeColor: colors.greens.primary,
                                strokeOpacity: 1,
                            },
                        }}
                    />
                )}
                {liveTracking && (
                    <LastUpdatedControl mapRef={mapRef} lastUpdated={location?.lastUpdated} onRefresh={() => {}} />
                )}
            </GoogleMap>
            {/*
             * Says out loud what the shading means. Without it a green blob reads just as easily as
             * a delivery zone or a service area, and the pin in the middle of it implies a precision
             * the circle deliberately does not have.
             *
             * Overlaid rather than stacked under the map: the map fills a fixed-height container, so
             * anything taking up flow height here would shrink it.
             */}
            {hideFacility && (
                <span
                    css={css`
                        ${typography.sansSerif}

                        position: absolute;
                        bottom: 8px;
                        left: 50%;
                        transform: translateX(-50%);
                        padding: 4px 10px;
                        border-radius: 4px;
                        background-color: rgba(250, 250, 250, 0.9);
                        color: ${colors.greys.secondary};
                        font-size: 12px;
                        font-weight: 500;
                        white-space: nowrap;
                        pointer-events: none;
                    `}
                >
                    Approximate facility area
                </span>
            )}
        </div>
    );
};

export default React.memo(TrackingMap);
